import { apiWithoutAuth, urlReplace } from '@shared';

import type { ApiResponse } from '@/shared/types/api';

import { exchangePaths, marketSegments, upbitPaths } from './paths';

import type { Exchange } from '../types/exchange';
import type { MarketCandle } from '../types';

export interface CryptoInfo {
  code: string;
  name: string;
  contents: string | null;
  repository: string | null;
  mainSite: string | null;
  source: string | null;
}

export const findMarkets = async () => {
  const response = await apiWithoutAuth.get(upbitPaths.markets, {
    params: {
      isDetails: false,
    },
  });

  return response.data?.result || response.data;
};

export const findMarketCandles = async ({ market, to, count, interval }: MarketCandle) => {
  const url = `${upbitPaths.candles}/${interval}`;

  const response = await apiWithoutAuth.get(url, {
    params: {
      market,
      count,
      to,
    },
  });

  return response.data?.result || response.data;
};

export const findMarketCandlesWithUnit = async ({ market, to, count, unit }: Omit<MarketCandle, 'interval'>) => {
  const url = `${upbitPaths.candlesByMinute}/${unit}`;

  const response = await apiWithoutAuth.get(url, {
    params: {
      market,
      count,
      to,
    },
  });

  const result = response.data?.result || response.data;
  const data = result?.list || result;

  return {
    data,
    initTime: data?.length > 0 ? new Date(data[data.length - 1].candle_date_time_kst).toISOString() : new Date().toISOString(),
  };
};

export const findMarketTicker = async (marketName: string) => {
  const url = urlReplace(upbitPaths.ticker, [[marketSegments.marketName, marketName]]);

  const response = await apiWithoutAuth.get(url);
  return response.data?.result || response.data;
};

export const findMarketTickers = async (marketNames: string[]) => {
  const markets = marketNames.join(',');

  const response = await apiWithoutAuth.get(`${upbitPaths.tickerMultiple}`, {
    params: {
      markets,
    },
  });

  return response.data?.result || response.data;
};

// --- 멀티 거래소 fetcher ---

export const findMarketsByExchange = async (exchange: Exchange) => {
  const response = await apiWithoutAuth.get(exchangePaths[exchange].markets, {
    params: { isDetails: false },
  });

  return response.data?.result || response.data;
};

export const findMarketTickersByExchange = async (exchange: Exchange, marketNames: string[]) => {
  const markets = marketNames.join(',');

  const response = await apiWithoutAuth.get(exchangePaths[exchange].ticker, {
    params: { markets },
  });

  return response.data?.result || response.data;
};

// --- 도미넌스 ---

export interface DominanceResponse {
  dominanceList: { symbol: string; dominance: number }[];
  totalMarketCap: number;
  marketCapChangePercentage24h: number;
  updatedAt: number;
}

export const fetchDominance = async (): Promise<DominanceResponse> => {
  const response = await apiWithoutAuth.get('/api/dominance');
  return response.data?.result || response.data;
};

// --- Top Movers ---

export interface TopMoverItem {
  market: string;
  tradePrice: number;
  openingPrice: number;
  highPrice: number;
  lowPrice: number;
  signedChangeRate: number;
  signedChangePrice: number;
  change: string;
  accTradeVolume24h: number;
  accTradePrice24h: number | null;
  timestamp: number;
}

export interface TopMoversResponse {
  topGainers: TopMoverItem[];
  topLosers: TopMoverItem[];
}

export const fetchTopMovers = async (): Promise<TopMoversResponse> => {
  const response = await apiWithoutAuth.get('/api/api/market/top-movers');
  return response.data?.result || response.data;
};

// --- 코인 정보 ---

export const fetchCryptoInfo = async (code: string): Promise<CryptoInfo> => {
  const { data } = await apiWithoutAuth.get<ApiResponse<CryptoInfo>>(`/api/coins/${code}`);
  return data.result;
};
